import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuid } from "uuid";
import { ForwardMessageResult, ForwardRecipient } from "../../models/ForwardRecipient";
import { Message } from "../../models/Message";
import { forwardMessage, getForwardRecipients } from "../../services/chatServices";

export const maxForwardRecipients = 20;
const recipientsPerPage = 20;
const searchDebounceMs = 350;

export const useForwardMessageModalController = (
  sourceChatId: string,
  sourceMessagesInput: Message | Array<Message> | null,
  open: boolean,
) => {
  const sourceMessages = useMemo(() => Array.isArray( sourceMessagesInput ) ? sourceMessagesInput : sourceMessagesInput ? [ sourceMessagesInput ] : [], [ sourceMessagesInput ]);
  const primarySourceMessage = sourceMessages[ 0 ] ?? null;
  const sourceMessageIDs = sourceMessages.map(( message ) => message.id ).join("|");
  const [ search, setSearchValue ] = useState( "" );
  const [ debouncedSearch, setDebouncedSearch ] = useState( "" );
  const [ page, setPage ] = useState( 1 );
  const [ total, setTotal ] = useState( 0 );
  const [ recipients, setRecipients ] = useState<Array<ForwardRecipient>>([]);
  const [ selected, setSelected ] = useState<Map<string, ForwardRecipient>>( new Map());
  const [ submittedRecipients, setSubmittedRecipients ] = useState<Map<string, ForwardRecipient>>( new Map());
  const [ results, setResults ] = useState<Array<ForwardMessageResult> | null>( null );
  const [ loading, setLoading ] = useState( false );
  const [ submitting, setSubmitting ] = useState( false );
  const [ selectionLimitReached, setSelectionLimitReached ] = useState( false );
  const [ submitError, setSubmitError ] = useState<string | null>( null );
  const requestSequenceRef = useRef( 0 );
  const selectedRef = useRef<Map<string, ForwardRecipient>>( new Map());
  const submittingRef = useRef( false );
  const operationRequestIDRef = useRef<string | null>( null );

  const updateSelected = useCallback(( next: Map<string, ForwardRecipient> ) => {
    selectedRef.current = next;
    setSelected( next );
  }, []);

  useEffect(() => {
    requestSequenceRef.current++;
    setSearchValue( "" );
    setDebouncedSearch( "" );
    setPage( 1 );
    setTotal( 0 );
    setRecipients( [] );
    updateSelected( new Map());
    setSubmittedRecipients( new Map());
    setResults( null );
    setLoading( false );
    submittingRef.current = false;
    setSubmitting( false );
    setSelectionLimitReached( false );
    setSubmitError( null );
    operationRequestIDRef.current = open && primarySourceMessage ? uuid() : null;
    // The source ID is the operation boundary; other message fields may be refreshed by realtime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ open, sourceChatId, sourceMessageIDs, updateSelected ]);

  useEffect(() => {
    const timeoutID = window.setTimeout(() => {
      setDebouncedSearch( search.trim());
      setPage( 1 );
    }, searchDebounceMs );

    return () => window.clearTimeout( timeoutID );
  }, [ search ]);

  useEffect(() => {
    if( !open || !sourceChatId || !primarySourceMessage ) return;

    const sequence = ++requestSequenceRef.current;
    setLoading( true );

    void getForwardRecipients({
      chatId: sourceChatId,
      search: debouncedSearch,
      page,
      perPage: recipientsPerPage,
    }).then(( response ) => {
      if( sequence !== requestSequenceRef.current ) return;

      if( response.data === false ) {
        setRecipients( [] );
        setTotal( 0 );
        return;
      }

      setRecipients( response.data.results );
      setTotal( response.data.total );
    }).finally(() => {
      if( sequence === requestSequenceRef.current ) setLoading( false );
    });
  }, [ open, sourceChatId, primarySourceMessage, debouncedSearch, page ]);

  const setSearch = useCallback(( value: string ) => {
    setSearchValue( value );
  }, []);

  const toggleRecipient = useCallback(( recipient: ForwardRecipient ): boolean => {
    const next = new Map( selectedRef.current );

    if( next.has( recipient.id )) {
      next.delete( recipient.id );
      setSelectionLimitReached( false );
      updateSelected( next );
      return true;
    }

    if( next.size >= maxForwardRecipients ) {
      setSelectionLimitReached( true );
      return false;
    }

    next.set( recipient.id, recipient );
    setSelectionLimitReached( next.size >= maxForwardRecipients );
    updateSelected( next );
    return true;
  }, [ updateSelected ]);

  const submit = useCallback(async (): Promise<boolean> => {
    if( submittingRef.current || sourceMessages.length === 0 || !sourceChatId || selectedRef.current.size === 0 ) return false;

    submittingRef.current = true;
    setSubmitting( true );
    setSubmitError( null );

    const operationRequestID = operationRequestIDRef.current ?? uuid();
    operationRequestIDRef.current = operationRequestID;
    const operationRecipients = new Map( selectedRef.current );

    try {
      const aggregate = new Map<string, ForwardMessageResult & { sentCount: number; failedCount: number }>();
      Array.from( operationRecipients.keys()).forEach(( contactId ) => {
        aggregate.set( contactId, { contactId, status: "SENT", sentCount: 0, failedCount: 0 });
      });

      for( const sourceMessage of sourceMessages ) {
        const response = await forwardMessage({
          chatId: sourceChatId,
          messageId: sourceMessage.id,
          requestId: operationRequestID,
          contactIds: Array.from( operationRecipients.keys()),
        });

        if( response.data === false ) {
          aggregate.forEach(( result ) => {
            result.status = "FAILED";
            result.failedCount += 1;
            result.message = "Não foi possível encaminhar esta mensagem.";
          });
          continue;
        }

        response.data.results.forEach(( result ) => {
          const current = aggregate.get( result.contactId );
          if( !current ) return;
          if( result.status === "FAILED" ) {
            current.status = "FAILED";
            current.failedCount += 1;
            current.errorCode = result.errorCode;
            current.message = result.message || "Não foi possível encaminhar esta mensagem.";
          } else {
            current.sentCount += 1;
            current.serviceId = result.serviceId || current.serviceId;
            current.messageId = result.messageId || current.messageId;
          }
        });
      }

      const nextResults = Array.from( aggregate.values()).map(({ sentCount, failedCount, ...result }) => ({
        ...result,
        status: failedCount > 0 ? "FAILED" as const : "SENT" as const,
        message: failedCount > 0
          ? sourceMessages.length > 1 ? `${ sentCount } de ${ sourceMessages.length } mensagens encaminhadas.` : result.message
          : result.message,
      }));

      setSubmittedRecipients( operationRecipients );
      setResults( nextResults );

      const failedIDs = new Set( nextResults
        .filter(( result ) => result.status === "FAILED" )
        .map(( result ) => result.contactId ));
      updateSelected( new Map(
        Array.from( operationRecipients.entries()).filter(([ contactID ]) => failedIDs.has( contactID )),
      ));
      setSelectionLimitReached( false );
      return true;
    } finally {
      submittingRef.current = false;
      setSubmitting( false );
    }
  }, [ sourceChatId, sourceMessages, updateSelected ]);

  const startNewOperation = useCallback(() => {
    operationRequestIDRef.current = uuid();
    setResults( null );
    setSubmittedRecipients( new Map());
    setSubmitError( null );
    setSelectionLimitReached( selectedRef.current.size >= maxForwardRecipients );
  }, []);

  return useMemo(() => ({
    search,
    setSearch,
    page,
    setPage,
    total,
    recipients,
    selectedRecipients: Array.from( selected.values()),
    submittedRecipients,
    results,
    sourceMessageCount: sourceMessages.length,
    loading,
    submitting,
    selectionLimitReached,
    submitError,
    toggleRecipient,
    submit,
    startNewOperation,
    pageCount: Math.ceil( total / recipientsPerPage ),
  }), [ search, setSearch, page, total, recipients, selected, submittedRecipients, results, sourceMessages.length, loading, submitting, selectionLimitReached, submitError, toggleRecipient, submit, startNewOperation ]);
};
